import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileText, AlertCircle, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const InspectorContractPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [contractText, setContractText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);

  // Signature state
  const [sigName, setSigName] = useState("");
  const [sigDate, setSigDate] = useState(() => new Date().toLocaleDateString("en-US"));
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/inspector-contract/${token}`);
        setData(res.data);
        setSigName(res.data.inspector_name || "");
        if (res.data.status === "Contract Signed") { setSigned(true); }
        if (res.data.contract_url) {
          const textRes = await fetch(`${BASE}${res.data.contract_url}`);
          setContractText(await textRes.text());
        }
      } catch (e) { setError(e.response?.data?.detail || "Contract not found"); }
      setLoading(false);
    };
    if (token) load();
  }, [token]);

  // Canvas drawing for signature
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const getPos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };

    const start = (e) => { e.preventDefault(); isDrawing.current = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!isDrawing.current) return; e.preventDefault(); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { isDrawing.current = false; };

    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("mouseup", end);
    canvas.addEventListener("mouseleave", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);

    return () => {
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      canvas.removeEventListener("mouseup", end);
      canvas.removeEventListener("mouseleave", end);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
    };
  }, [loading, signed]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const isCanvasBlank = () => {
    const canvas = canvasRef.current;
    if (!canvas) return true;
    const ctx = canvas.getContext("2d");
    const px = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 3; i < px.length; i += 4) { if (px[i] > 0) return false; }
    return true;
  };

  const handleSign = async () => {
    if (!sigName.trim()) { alert("Please type your full name"); return; }
    if (!agreed) { alert("Please check the agreement checkbox"); return; }
    if (isCanvasBlank()) { alert("Please draw your signature"); return; }

    setSubmitting(true);
    try {
      // Convert canvas to blob
      const canvas = canvasRef.current;
      const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));

      const fd = new FormData();
      fd.append("signature_image", blob, "signature.png");
      fd.append("typed_name", sigName.trim());
      fd.append("signed_date", sigDate);

      await axios.post(`${API}/inspector-contract/${token}/e-sign`, fd);
      setSigned(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit signature. Please try again.");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D1A054]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <img src="/aarifi-logo.png" alt="AARIFI" className="h-16 mx-auto mb-4 object-contain" />
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center" data-testid="contract-signed-success">
          <img src="/aarifi-logo.png" alt="AARIFI" className="h-16 mx-auto mb-4 object-contain" />
          <CheckCircle className="h-12 w-12 text-[#6B8F62] mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Contract Signed</h1>
          <p className="text-sm text-gray-500">Thank you, <span className="font-semibold">{data?.inspector_name}</span>. Your signed contract has been submitted and saved automatically.</p>
          <p className="text-xs text-gray-400 mt-4">You will be contacted with next steps regarding your activation.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E8] p-4" data-testid="inspector-contract-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="/aarifi-logo.png" alt="AARIFI Services LLC" className="h-20 mx-auto mb-3 object-contain" data-testid="aarifi-logo" />
          <h1 className="text-xl font-bold text-gray-800">Independent Contractor Agreement</h1>
          <p className="text-sm text-gray-500 mt-1">Contract for <span className="font-semibold text-[#2F5D8A]">{data?.inspector_name}</span></p>
        </div>

        {/* Contract content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
          <pre className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }} data-testid="contract-text">
            {contractText}
          </pre>
        </div>

        {/* E-Signature Section */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-[#D1A054]/30 p-5 space-y-4" data-testid="e-signature-section">
          <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
            <FileText className="h-5 w-5 text-[#D1A054]" />
            <h2 className="text-base font-bold text-gray-800">Electronic Signature</h2>
          </div>

          {/* Typed Name */}
          <div>
            <label className="text-xs font-medium text-gray-600">Full Name (typed) *</label>
            <input type="text" value={sigName} onChange={e => setSigName(e.target.value)}
              className="w-full mt-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 font-medium focus:outline-none focus:border-[#D1A054]"
              placeholder="Type your full legal name" data-testid="sig-name-input" />
          </div>

          {/* Signature Canvas */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-600">Signature *</label>
              <button onClick={clearSignature} className="text-[10px] text-[#2F5D8A] hover:underline font-medium" data-testid="clear-signature-btn">Clear</button>
            </div>
            <div className="mt-1 border-2 border-gray-200 rounded-lg overflow-hidden bg-white" style={{ touchAction: "none" }}>
              <canvas ref={canvasRef} className="w-full cursor-crosshair" style={{ height: "120px", display: "block" }} data-testid="signature-canvas" />
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Draw your signature above using your finger or mouse</p>
          </div>

          {/* Date */}
          <div>
            <label className="text-xs font-medium text-gray-600">Date</label>
            <input type="text" value={sigDate} onChange={e => setSigDate(e.target.value)}
              className="w-full mt-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 bg-gray-50"
              data-testid="sig-date-input" />
          </div>

          {/* Agreement Checkbox */}
          <label className="flex items-start gap-3 cursor-pointer" data-testid="agree-checkbox-label">
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#6B8F62] focus:ring-[#6B8F62]" data-testid="agree-checkbox" />
            <span className="text-xs text-gray-600 leading-relaxed">
              I, <span className="font-bold text-gray-800">{sigName || "___"}</span>, confirm that I have read and agree to the terms and conditions of this Independent Contractor Agreement. I understand that this electronic signature is legally binding.
            </span>
          </label>

          {/* Sign & Submit */}
          <button onClick={handleSign} disabled={submitting || !agreed || !sigName.trim()}
            className="w-full py-3.5 bg-[#6B8F62] hover:bg-[#5A7B52] text-white font-bold text-base rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 shadow-sm"
            data-testid="sign-submit-btn">
            <CheckCircle className="h-5 w-5" />
            {submitting ? "Submitting..." : "I Agree & Sign Contract"}
          </button>
        </div>

        <p className="text-center text-[10px] text-gray-400 mt-6">AARIFI Services LLC — Electronic Contract Signing</p>
      </div>
    </div>
  );
};

export default InspectorContractPage;
